import {
  Paper,
  Stack,
  Typography,
  Button,
  Chip,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  BookmarkAdd as BookmarkAddIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { FileEdit, Lightbulb, AlertTriangle } from 'lucide-react';
import type { DREVersion } from '../../types/version';

interface CurrentWorkCardProps {
  baseVersion?: DREVersion;
  isDirty: boolean;
  changeCount?: number;
  onSave: () => void;
  onSaveVersion: () => void;
  isSaving?: boolean;
}

export function CurrentWorkCard({
  baseVersion,
  isDirty,
  changeCount = 0,
  onSave,
  onSaveVersion,
  isSaving = false,
}: CurrentWorkCardProps) {
  return (
    <Paper
      sx={{
        p: 2.5,
        background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
        border: '2px solid',
        borderColor: isDirty ? 'warning.main' : 'success.main',
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <FileEdit size={20} />
            <Typography variant="h6" fontWeight="bold">
              Trabalho Atual
            </Typography>
            {isDirty ? (
              <Chip
                icon={<EditIcon fontSize="small" />}
                label={`Modificado${changeCount > 0 ? ` (${changeCount})` : ''}`}
                color="warning"
                size="small"
              />
            ) : (
              <Chip
                icon={<CheckCircleIcon fontSize="small" />}
                label="Salvo"
                color="success"
                size="small"
              />
            )}
          </Stack>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1}>
            {isDirty && (
              <Button
                variant="outlined"
                size="small"
                startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={onSave}
                disabled={isSaving}
              >
                Salvar
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<BookmarkAddIcon />}
              onClick={onSaveVersion}
              disabled={isSaving}
            >
              Nova Versão
            </Button>
          </Stack>
        </Stack>

        {/* Base Version Info */}
        {baseVersion && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                Base:
              </Typography>
              <Chip
                label={`v${baseVersion.versionNumber} - ${baseVersion.name}`}
                size="small"
                variant="outlined"
              />
              {baseVersion.description && (
                <Typography variant="caption" color="text.secondary">
                  • {baseVersion.description}
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {/* Help Text */}
        {!isDirty && !baseVersion && (
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Lightbulb size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">
              Comece a editar os dados do DRE. Suas alterações serão salvas automaticamente no banco de dados.
              Crie uma versão nomeada quando quiser marcar um snapshot importante.
            </Typography>
          </Stack>
        )}

        {isDirty && (
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <AlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">
              Você tem alterações não salvas. Clique em "Salvar" para persistir no banco de dados ou "Nova Versão"
              para criar um snapshot nomeado com as alterações atuais.
            </Typography>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
